/**
 * The pad: a held chord under the band from Strong up, voiced the way a keyboard player lays one.
 *
 * A leaf, like chordVoicing.js: it reads chord symbols and hands back MIDI numbers.
 */

import { getChordNotes } from './chordNotes';
import { getPitchClass } from './chordVoicing';

/**
 * The pads a style can name: the instruments each plays on at once, balanced by their presets' own
 * levels. `pulse` restrikes the chord on the eighths instead of holding it.
 */
export const PAD_SOUNDS = {
  // A synth bed for width and air, slow strings under it for body: the worship pad
  warm: { instruments: ['synth-worship-pad', 'pad-strings-slow'] },
  strings: { instruments: ['pad-strings'] },
  // The keyboard's string patch on Latin praise, bright enough to carry over a driving band
  bright: { instruments: ['pad-synth-strings'] },
  // Gospel and the corito, on a drawbar organ
  organ: { instruments: ['pad-drawbar-organ'] },
  church: { instruments: ['pad-church-organ'] },
  pulse: { instruments: ['synth-pulse-pad'], pulse: true },
};

export const getPadSound = (id) => PAD_SOUNDS[id] || null;

// A pulsing pad's eighths: on the beat a little over the & between, each let go before the next
const PULSE_STEP = 0.5;
const PULSE_HOLD = 0.4;
const PULSE_OFFBEAT = 0.8;

// The low voice, from A2 to G#3: under the piano's hands and over the bass guitar
const LOW_FROM_MIDI = 45;
// The upper voices sit in an octave from C4, moved as little as they can from chord to chord
const UPPER_FROM_MIDI = 60;
const UPPER_CENTRE_MIDI = 66;

// Color first: the third (or the sus), then the seventh or sixth, the ninth, the fifth, the root
const UPPER_PRIORITY = [[3, 4, 2, 5], [10, 11, 9], [14, 17], [7, 6, 8], [0]];
const UPPER_VOICES = 3;

const centre = (notes) => notes.reduce((total, midi) => total + midi, 0) / notes.length;

const upperClasses = (intervals, rootClass, bassClass) => {
  const classes = [];

  UPPER_PRIORITY.forEach((group) => {
    const interval = group.find((wanted) => intervals.includes(wanted));

    if (interval === undefined) return;

    const pitch = (rootClass + interval) % 12;

    // A slash chord's bass is already under it
    if ((bassClass === rootClass || pitch !== bassClass) && !classes.includes(pitch)) classes.push(pitch);
  });

  // A chord short of tones, a power chord or one over its own third, doubles its root on top
  while (classes.length > 0 && classes.length < UPPER_VOICES) classes.push(rootClass);

  return classes.slice(0, UPPER_VOICES);
};

/**
 * One chord as the pad voices it: its bass low, the fifth over that where the root is the bass, and
 * three upper voices in close position, turned to whichever inversion sits nearest the last chord's.
 *
 * @param {string} chord - A chord symbol
 * @param {number[]} previous - The last chord's upper voices, or null
 * @returns {{ low: number[], upper: number[] }} Both empty for a symbol with no readable root
 */
export const getPadVoicing = (chord, previous = null) => {
  const { root, intervals, bassNote } = getChordNotes(chord);
  const rootClass = getPitchClass(root);

  if (rootClass === null) return { low: [], upper: [] };

  const bassClass = (bassNote ? getPitchClass(bassNote) : null) ?? rootClass;
  const bass = LOW_FROM_MIDI + ((bassClass - (LOW_FROM_MIDI % 12) + 12) % 12);
  const low = bassClass === rootClass && intervals.includes(7) ? [bass, bass + 7] : [bass];

  const classes = upperClasses(intervals, rootClass, bassClass);
  const stacks = classes.map((_, k) => {
    const turned = [...classes.slice(k), ...classes.slice(0, k)];
    const stack = [];

    turned.forEach((pitch) => {
      let midi = UPPER_FROM_MIDI + ((pitch - (UPPER_FROM_MIDI % 12) + 12) % 12);

      while (stack.length > 0 && midi <= stack[stack.length - 1]) midi += 12;
      stack.push(midi);
    });

    return stack;
  });

  const target = previous?.length ? centre(previous) : UPPER_CENTRE_MIDI;
  const upper = stacks.reduce(
    (best, stack) => (Math.abs(centre(stack) - target) < Math.abs(centre(best) - target) ? stack : best),
    stacks[0] || [],
  );

  return { low, upper };
};

/**
 * Two notes on the same key where one ends as the other starts, as one: a pad holds a note the
 * next chord shares rather than striking it again.
 */
export const tieCommonTones = (notes) => {
  const sounding = new Map();
  const tied = [];

  [...notes]
    .sort((a, b) => a.time - b.time)
    .forEach((note) => {
      const key = `${note.instrumentId || ''}:${note.midi}`;
      const held = sounding.get(key);

      if (held && Math.abs(held.time + held.duration - note.time) < 1e-6) {
        held.duration = note.time + note.duration - held.time;

        return;
      }

      const copy = { ...note };

      sounding.set(key, copy);
      tied.push(copy);
    });

  return tied;
};

/**
 * The pad under a run of strikes. Each chord is held for its whole length, or pulsed across it,
 * rests stop it, and a melody bar has none.
 *
 * @param {object[]} strikes - From getChordStrikes
 * @param {object} options - { secondsPerBeat, velocity, top, pulse }. `top` adds the octave over the
 *   top voice.
 * @returns {Array<{midi: number, time: number, duration: number, velocity: number}>}
 */
export const getPadNotes = (strikes, { secondsPerBeat, velocity, top = false, pulse = false }) => {
  const notes = [];
  let previous = null;

  strikes.forEach(({ chord, time, beats, melody }) => {
    if (melody) return;

    const { low, upper } = getPadVoicing(chord, previous);

    if (upper.length === 0) return;

    previous = upper;

    const voices = [...low, ...upper, ...(top ? [upper[upper.length - 1] + 12] : [])];

    if (!pulse) {
      voices.forEach((midi) => notes.push({ midi, time, duration: beats * secondsPerBeat, velocity }));

      return;
    }

    for (let at = 0; at < beats - 1e-6; at += PULSE_STEP) {
      const hold = Math.min(PULSE_HOLD, beats - at) * secondsPerBeat;
      const level = at % 1 === 0 ? 1 : PULSE_OFFBEAT;

      voices.forEach((midi) =>
        notes.push({ midi, time: time + at * secondsPerBeat, duration: hold, velocity: velocity * level }),
      );
    }
  });

  return pulse ? notes : tieCommonTones(notes);
};
